import React, { useEffect, useRef, useState } from 'react'
import { useExclusiveMenu } from '../../hooks/useExclusiveMenu'
import { FormControl } from '@mui/material'
import Select, { SingleValue } from 'react-select'
import { SelectChangeEvent } from '@mui/material/Select'
import { useSelector } from 'react-redux'
import { getProjetbyMission } from '../../redux/projets/projetActions'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import { useParams } from 'react-router-dom'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { incyiaSelectStyles } from '../../utils/incyiaSelectStyles'
import { createOptionProps } from '../../components/common/CreateOptionMenu'

interface SelectProjetProps {
  value: string
  onChange: (event: SelectChangeEvent<string>) => void
  MissionId: string
  disablePortalMenu?: boolean
  onCreateNew?: (search: string) => void
  reloadKey?: number
  /** Keep the current project, in addition to the add-task route lock. */
  disabled?: boolean
}

const SelectProjet: React.FC<SelectProjetProps> = ({
  value,
  onChange,
  MissionId,
  disablePortalMenu = false,
  onCreateNew,
  reloadKey = 0,
  disabled = false,
}) => {
  const menu = useExclusiveMenu(!disablePortalMenu)
  const dispatch: AppDispatch = useAppDispatch()
  const username = useSelector(selectUsername)
  const { idprojet } = useParams<{ idprojet: string; id: string }>()
  const roles = useSelector((state: any) => state.auth.roles)
  const { t } = useTranslation()

  const ProjetsList = useSelector(
    (state: RootState) => state.projet.ProjetResponse?.projets
  )

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [optionsLoading, setOptionsLoading] = useState(false)


  useEffect(() => {
    if (!MissionId) {
      setOptionsLoading(false)
      return
    }
    let active = true
    setOptionsLoading(true)
    dispatch(getProjetbyMission({ MissionId })).finally(() => {
      if (active) setOptionsLoading(false)
    })
    return () => {
      active = false
    }
  }, [dispatch, MissionId, reloadKey])

  const filteredProjets =
    hasRole(roles, UserRole.ADMIN) || hasRole(roles, UserRole.RSSI)
      ? ProjetsList
      : idprojet
        ? ProjetsList
        : ProjetsList?.filter(
            (Projet) =>
              Projet.CreatedBy?.toLocaleLowerCase() ===
              username?.toLocaleLowerCase()
          )

  const options =
    filteredProjets?.map((Projet) => ({
      value: Projet._id || '',
      label: Projet.ProjetName || '',
    })) || []

  const selectedOption = options.find((opt) => opt.value === value) || null

  const handleSelectChange = (
    selected: SingleValue<{ value: string; label: string }>
  ) => {
    onChange({
      target: { name: 'ProjetId', value: selected ? selected.value : '' },
    } as SelectChangeEvent<string>)
  }

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <FormControl fullWidth style={{ width: '100%' }}>
        <Select
          value={selectedOption}
          onChange={handleSelectChange}
          options={options}
          isLoading={optionsLoading}
          placeholder={
            !MissionId
              ? t('actionImport.context.pickMissionFirst')
              : optionsLoading
                ? t('selectOptions.loading')
                : t('projects')
          }
          isDisabled={disabled || !!idprojet || optionsLoading || !MissionId}
          noOptionsMessage={() =>
            !MissionId
              ? t('actionImport.context.pickMissionFirst')
              : t('You have not created any projects yet')
          }
          {...menu}
          styles={incyiaSelectStyles}
          {...createOptionProps(
            onCreateNew && MissionId
              ? { label: t('quickCreate.new.project'), onCreate: onCreateNew }
              : undefined
          )}
        />
      </FormControl>
    </div>
  )
}

export default SelectProjet
